import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import { loadPrefs, savePrefs } from "../prefs";
import { ProfileAvatar } from "./ProfilesPage";

const AUDIO_OPTIONS = [
  { value: "", label: "Automático (del vídeo)" },
  { value: "es", label: "Español" },
  { value: "en", label: "Inglés" },
  { value: "pt", label: "Portugués" },
  { value: "ja", label: "Japonés" },
];

const SUB_OPTIONS = [
  { value: "off", label: "Sin subtítulos" },
  { value: "", label: "Automático (del vídeo)" },
  { value: "es", label: "Español" },
  { value: "en", label: "Inglés" },
  { value: "pt", label: "Portugués" },
  { value: "ja", label: "Japonés" },
];

const TABS = [
  { id: "profile", label: "Perfil", icon: "user" },
  { id: "appearance", label: "Apariencia", icon: "moon" },
  { id: "playback", label: "Reproducción", icon: "play" },
  { id: "account", label: "Cuenta", icon: "key" },
];

function TabIcon({ name }) {
  const props = {
    className: "settings-tab-icon",
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.85,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "user":
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5.5 19c1.2-3.2 3.4-4.8 6.5-4.8S17.3 15.8 18.5 19" />
        </svg>
      );
    case "moon":
      return (
        <svg {...props}>
          <path d="M20 14.5A7.5 7.5 0 0 1 9.5 4 7.2 7.2 0 1 0 20 14.5z" />
        </svg>
      );
    case "play":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2.5" />
          <path d="M10 9.5v5l4.5-2.5z" fill="currentColor" stroke="none" />
        </svg>
      );
    case "key":
      return (
        <svg {...props}>
          <circle cx="8" cy="12" r="3.2" />
          <path d="M10.8 12H20v2.2M16.5 12v2.4" />
        </svg>
      );
    default:
      return null;
  }
}

export default function SettingsPage() {
  const { token, user, profile, setUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.name]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  function switchTab(next) {
    if (next === tab) return;
    setTab(next);
    setError("");
    setMsg("");
  }

  function updatePref(partial) {
    const next = savePrefs(partial);
    setPrefs(next);
    setToast("Preferencias guardadas");
    setError("");
  }

  async function saveAccount(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const body = { name: name.trim() };
      if (newPassword || confirmPassword || currentPassword) {
        if (newPassword.length < 6) {
          throw new Error("La nueva contraseña debe tener al menos 6 caracteres.");
        }
        if (newPassword !== confirmPassword) {
          throw new Error("Las contraseñas nuevas no coinciden.");
        }
        if (!currentPassword) {
          throw new Error("Indica tu contraseña actual.");
        }
        body.current_password = currentPassword;
        body.new_password = newPassword;
      }
      const updated = await api("/api/auth/me", {
        method: "PUT",
        token,
        body,
      });
      setUser(updated);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMsg(body.new_password ? "Cuenta y contraseña actualizadas." : "Nombre de usuario guardado.");
      setToast("Cambios guardados");
    } catch (err) {
      setError(err.message || "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className="page settings-page">
        <header className="settings-hero settings-anim settings-anim-hero">
          <div className="settings-hero-copy">
            <p className="brand">Cuenta</p>
            <h1>Mis configuraciones</h1>
            <p className="muted">
              Personaliza el tema, el idioma y tus datos de acceso.
            </p>
          </div>
          {profile ? (
            <div className="settings-hero-chip">
              <ProfileAvatar profile={profile} size={48} className="topbar-avatar" />
              <div>
                <strong>{profile.name}</strong>
                <span className="muted">
                  {profile.is_kids ? "Perfil infantil" : "Perfil activo"}
                </span>
              </div>
            </div>
          ) : null}
        </header>

        <div className="settings-shell settings-anim settings-anim-shell">
          <nav className="settings-tabs" role="tablist" aria-label="Secciones">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`settings-tab ${tab === item.id ? "active" : ""}`}
                onClick={() => switchTab(item.id)}
              >
                <TabIcon name={item.icon} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="settings-panel" role="tabpanel" key={tab}>
            {tab === "profile" ? (
              <section className="settings-panel-inner">
                <div className="settings-panel-head">
                  <h2>Perfil activo</h2>
                  <p className="muted">
                    Cambia de usuario o vuelve al catálogo sin cerrar la sesión.
                  </p>
                </div>
                {profile ? (
                  <div className="settings-profile-card">
                    <ProfileAvatar profile={profile} size={88} />
                    <div>
                      <strong>{profile.name}</strong>
                      <p className="muted">
                        {profile.is_kids ? "Perfil infantil" : "Perfil estándar"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="muted">No hay perfil seleccionado.</p>
                )}
                <div className="settings-actions">
                  <button type="button" onClick={() => navigate("/profiles")}>
                    Cambiar de usuario
                  </button>
                  <Link to="/" className="ghost">
                    Volver al inicio
                  </Link>
                </div>
              </section>
            ) : null}

            {tab === "appearance" ? (
              <section className="settings-panel-inner">
                <div className="settings-panel-head">
                  <h2>Apariencia</h2>
                  <p className="muted">Elige cómo se ve Casasm en tu dispositivo.</p>
                </div>
                <div className="settings-theme-grid" role="group" aria-label="Tema">
                  <button
                    type="button"
                    className={`settings-theme-card ${prefs.theme === "dark" ? "active" : ""}`}
                    onClick={() => updatePref({ theme: "dark" })}
                  >
                    <span className="settings-theme-preview dark" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <strong>Oscuro</strong>
                    <span className="muted">Ideal para ver de noche</span>
                  </button>
                  <button
                    type="button"
                    className={`settings-theme-card ${prefs.theme === "light" ? "active" : ""}`}
                    onClick={() => updatePref({ theme: "light" })}
                  >
                    <span className="settings-theme-preview light" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <strong>Claro</strong>
                    <span className="muted">Más luminoso y limpio</span>
                  </button>
                </div>
              </section>
            ) : null}

            {tab === "playback" ? (
              <section className="settings-panel-inner">
                <div className="settings-panel-head">
                  <h2>Reproducción</h2>
                  <p className="muted">
                    Preferencias globales de audio y subtítulos para todos los títulos.
                  </p>
                </div>
                <div className="settings-form settings-form-wide">
                  <label>
                    Idioma de audio
                    <select
                      value={prefs.audioLang}
                      onChange={(e) => updatePref({ audioLang: e.target.value })}
                    >
                      {AUDIO_OPTIONS.map((o) => (
                        <option key={`a-${o.value || "auto"}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Subtítulos
                    <select
                      value={prefs.subtitleLang}
                      onChange={(e) => updatePref({ subtitleLang: e.target.value })}
                    >
                      {SUB_OPTIONS.map((o) => (
                        <option key={`s-${o.value || "auto"}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>
            ) : null}

            {tab === "account" ? (
              <section className="settings-panel-inner">
                <div className="settings-panel-head">
                  <h2>Cuenta</h2>
                  <p className="muted">Nombre de usuario, contraseña y sesión.</p>
                </div>
                <div className="settings-account-meta">
                  <span className="settings-email">{user?.email}</span>
                  {isAdmin ? <span className="role-pill role-admin">Admin</span> : null}
                </div>
                <form className="settings-form" onSubmit={saveAccount}>
                  <label>
                    Nombre de usuario
                    <input
                      value={name}
                      maxLength={120}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre de usuario"
                      autoComplete="username"
                      required
                    />
                  </label>
                  <div className="settings-divider">Cambiar contraseña</div>
                  <label>
                    Contraseña actual
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="Solo si cambias la clave"
                    />
                  </label>
                  <div className="settings-password-grid">
                    <label>
                      Nueva contraseña
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={6}
                        placeholder="Mínimo 6 caracteres"
                      />
                    </label>
                    <label>
                      Confirmar
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </label>
                  </div>
                  {error ? <p className="error">{error}</p> : null}
                  {msg ? <p className="ok">{msg}</p> : null}
                  <div className="settings-actions">
                    <button type="submit" disabled={busy}>
                      {busy ? "Guardando…" : "Guardar cuenta"}
                    </button>
                    <button
                      type="button"
                      className="btn-danger-ghost"
                      onClick={() => {
                        logout();
                        navigate("/login", { replace: true });
                      }}
                    >
                      Cerrar sesión
                    </button>
                  </div>
                </form>
              </section>
            ) : null}
          </div>
        </div>

        {toast ? (
          <div className="settings-toast" role="status">
            {toast}
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
